import { IPublicClientApplication } from "@azure/msal-browser";
import axios from "axios";
import { format } from "date-fns";

import {
    axiosGetRequest,
    axiosPatchRequest,
    axiosPostRequest,
} from "../../api/ApiClient";

import GenericUtils from "../../utils/GenericUtils";
import { StatusSubprocess } from "../../model/Enums/StatusSubprocessEnum";
import { IFilter } from "../../context/interfaces/IFilterDataContext";
import { IPagination } from "../../components/IPagination";

interface ISelectedValues {
    campi: (string | number)[];
    escolas: (string | number)[];
    modalidades: (string | number)[];
    centrosResultado: (string | number)[];
    docentes: (string | number)[];
}

const route = "/docente/v1/";

const formatDate = (date: Date): string => {
    return format(date, "yyyy-MM-dd");
};

const generateDateFilter = (
    field: string,
    date: Date,
    operator: string
): string => {
    return `${field} ${operator} date:${formatDate(date)}`;
};

const formatArrayToQueryString = (
    list: (string | number)[],
    hasQuote: boolean
): string => {
    if (!list.length) return "";

    if (hasQuote) return list.map((s) => `"${s}"`).join(", ");

    return list.map((s) => `${s}`).join(", ");
};

const generateFilterURL = (filter: IFilter, pagination: IPagination) => {
    const campusList = GenericUtils.formatFilterToList(
        "campus",
        "name",
        filter
    );
    const schoolList = GenericUtils.formatFilterToList(
        "school",
        "name",
        filter
    );
    const modalityList = GenericUtils.formatFilterToList(
        "modality",
        "id",
        filter
    );
    const courseList = GenericUtils.formatFilterToList("course", "id", filter);
    const professorList = GenericUtils.formatFilterToList(
        "professor",
        "description",
        filter
    );

    const queryParams = [];

    if (filter.status) {
        if (
            filter.status === StatusSubprocess.WAITING_APPROVAL ||
            filter.status === StatusSubprocess.DONE
        ) {
            queryParams.push(
                `status in ("${StatusSubprocess.DONE}", "${StatusSubprocess.WAITING_APPROVAL}")`
            );
        } else {
            queryParams.push(`status="${filter.status}"`);
        }
    }

    if (filter.effectiveDate.initialDate) {
        queryParams.push(
            generateDateFilter(
                "dtVigenciaInicio",
                filter.effectiveDate.initialDate,
                ">="
            )
        );
    }

    if (filter.effectiveDate.finalDate) {
        queryParams.push(
            generateDateFilter(
                "dtVigenciaFim",
                filter.effectiveDate.finalDate,
                "<="
            )
        );
    }

    if (campusList.length) {
        queryParams.push(
            `campus in (${formatArrayToQueryString(campusList, true)})`
        );
    }

    if (schoolList.length) {
        queryParams.push(
            `escola in (${formatArrayToQueryString(schoolList, true)})`
        );
    }

    if (modalityList.length) {
        queryParams.push(
            `modalidadeId in (${formatArrayToQueryString(modalityList, false)})`
        );
    }

    if (courseList.length) {
        queryParams.push(
            `centroResultadoId in (${formatArrayToQueryString(
                courseList,
                false
            )})`
        );
    }

    if (professorList.length) {
        queryParams.push(
            `docente(matriculaAdp in (${formatArrayToQueryString(
                professorList,
                true
            )}))`
        );
    }

    return `?orderBy=docente.nome&SortOrder=ASCENDING&maxResults=${
        pagination.sizePage
    }&page=${pagination.actualPage}${
        queryParams.length ? `&query=${queryParams.join(" and ")}` : ""
    }`;
};

const getSubprocess = (
    filter: IFilter,
    pagination: IPagination,
    instance: IPublicClientApplication
) => {
    const URL = `${route}${"subprocessosjustificativastach"}${generateFilterURL(
        filter,
        pagination
    )}`;
    return axiosGetRequest(URL, GenericUtils.getAccessTokenDocente(), instance);
};

const getActivityRoute = (instance: IPublicClientApplication) => {
    return axiosGetRequest(
        `${route}${"atividades-docentes"}`,
        GenericUtils.getAccessTokenDocente(),
        instance
    );
};

const getSchoolsRoute = (
    instance: IPublicClientApplication,
    campusList: (string | number)[]
) => {
    return axiosGetRequest(
        `${route}escolas?orderBy=escola&sortOrder=ASCENDING&campusList=${formatArrayToQueryString(
            campusList,
            false
        )}&maxResults=999999`,
        GenericUtils.getAccessTokenDocente(),
        instance
    );
};

const getCoursesRoute = (
    instance: IPublicClientApplication,
    campusList: (string | number)[],
    schoolList: (string | number)[],
    modalityList: (string | number)[]
) => {
    return axiosGetRequest(
        `${route}centrosresultadofilter?sortOrder=ASCENDING&query=campus in (${formatArrayToQueryString(
            campusList,
            true
        )}) and escola in (${formatArrayToQueryString(
            schoolList,
            true
        )}) and modalidade(modalidadeId in (${formatArrayToQueryString(
            modalityList,
            false
        )}))&maxResults=999999`,
        GenericUtils.getAccessTokenDocente(),
        instance
    );
};

const getResultCampus = (instance: IPublicClientApplication) => {
    return axiosGetRequest(
        `${route}${"campi"}`,
        GenericUtils.getAccessTokenDocente(),
        instance
    );
};

const getProfessorRoute = (
    instance: IPublicClientApplication,
    courseList: (string | number)[]
) => {
    const query = courseList.length
        ? `&query=usuario(listaPerfis(idPerfil = 3)) and centroResultado(centroResultadoId in(${formatArrayToQueryString(
              courseList,
              false
          )}))`
        : "&query=usuario(listaPerfis(idPerfil = 3))";

    return axiosGetRequest(
        `${route}docentesfilter?orderBy=nome&sortOrder=ASCENDING${query}&maxResults=999999`,
        GenericUtils.getAccessTokenDocente(),
        instance
    );
};

const getModalityRoute = (
    instance: IPublicClientApplication,
    campusList: (string | number)[],
    schoolList: (string | number)[]
) => {
    return axiosGetRequest(
        `${route}modalidades?query=centroResultados(campus in (${formatArrayToQueryString(
            campusList,
            true
        )}) and escola in (${formatArrayToQueryString(
            schoolList,
            true
        )}))&maxResults=999999`,
        GenericUtils.getAccessTokenDocente(),
        instance
    );
};

const getProcess = (instance: IPublicClientApplication) => {
    const URL = `${route}processostach?sortOrder=DESCENDING`;

    return axiosGetRequest(URL, GenericUtils.getAccessTokenDocente(), instance);
};

const getSubprocessById = (id: string, instance: IPublicClientApplication) => {
    return axiosGetRequest(
        `${route}${"subprocessosjustificativastach"}/${id}`,
        GenericUtils.getAccessTokenDocente(),
        instance
    );
};

const cancelSubprocessById = (
    id: string,
    justification: string,
    instance: IPublicClientApplication
) => {
    const body = {
        justificativa: justification,
        status: StatusSubprocess.CANCELED,
    };

    return axiosPatchRequest(
        `${route}${"subprocessosjustificativastach"}/${id}`,
        body,
        GenericUtils.getAccessTokenDocente(),
        instance
    );
};

async function getSubprocessUploadLink(
    id: string,
    fileName: string,
    instance: IPublicClientApplication
) {
    return axiosGetRequest(
        `${route}${"subprocessosjustificativastach"}/${id}/file?fileName=${fileName}&fileAction=UPLOAD`,
        GenericUtils.getAccessTokenDocente(),
        instance
    );
}

async function uploadFileByUrl(url: string, file: File | undefined) {
    return axios.put(`${url}`, file, {
        headers: {
            "x-ms-blob-type": "BlockBlob",
        },
    });
}

async function justifySubprocess(
    id: string,
    fileName: string,
    justification: string,
    instance: IPublicClientApplication
) {
    const body = {
        anexoNome: fileName,
        justificativa: justification,
        status: StatusSubprocess.DONE,
    };

    return axiosPatchRequest(
        `${route}${"subprocessosjustificativastach"}/${id}`,
        body,
        GenericUtils.getAccessTokenDocente(),
        instance
    );
}

const createProcess = (
    selectedValues: ISelectedValues,
    instance: IPublicClientApplication
) => {
    return axiosPostRequest(
        `${route}${"processostach"}`,
        selectedValues,
        GenericUtils.getAccessTokenDocente(),
        instance
    );
};

const getDacPermission = async (instance: IPublicClientApplication) => {
    const URL = `${route}subprocessosjustificativastach/accessvalidate`;
    return axiosGetRequest(URL, GenericUtils.getAccessTokenDocente(), instance);
};

export default {
    getActivityRoute,
    getSchoolsRoute,
    getCoursesRoute,
    getResultCampus,
    getProfessorRoute,
    getModalityRoute,
    getProcess,
    getSubprocess,
    getSubprocessById,
    cancelSubprocessById,
    getSubprocessUploadLink,
    uploadFileByUrl,
    justifySubprocess,
    createProcess,
    getDacPermission,
};
